import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes } from 'react-icons/fa';
import { useLoginModalContext } from '@contexts/LoginModalContext';
import LoginSignUp from '@components/forms/LoginSignUp';
import { useTheme } from '@theme/useTheme';

const LoginModal: React.FC = () => {
  const { isLoginModalActive, setLoginModalActive } = useLoginModalContext();
  const { colors } = useTheme();
  const [errors, setErrors] = useState<string[]>([]);

  // Listen for custom events to open modal
  useEffect(() => {
    const handleOpenLoginModal = () => {
      setLoginModalActive(true);
    };

    window.addEventListener('openLoginModal', handleOpenLoginModal);

    return () => {
      window.removeEventListener('openLoginModal', handleOpenLoginModal);
    };
  }, [setLoginModalActive]);

  useEffect(() => {
    if (isLoginModalActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isLoginModalActive]);

  const closeModal = () => {
    setLoginModalActive(false);
    setErrors([]);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  return (
    <AnimatePresence>
      {isLoginModalActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-100 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md mx-auto shadow-2xl overflow-hidden border"
            style={{
              backgroundColor: colors.background,
              borderColor: colors.border,
              borderTopRightRadius: '2rem',
              borderBottomLeftRadius: '2rem'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 p-2 rounded-full transition-colors duration-200"
              style={{
                color: colors.text,
                backgroundColor: colors.error + '20'
              }}
              title="Close modal"
              aria-label="Close login modal"
            >
              <FaTimes size={20} />
            </button>

            {/* Modal Content */}
            <div className="p-6 pt-12">
              {/* Error Messages */}
              {errors.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 p-3 rounded-lg border"
                  style={{
                    backgroundColor: colors.error + '20',
                    borderColor: colors.error
                  }}
                >
                  {errors.map((error, index) => (
                    <p key={index} className="text-sm" style={{ color: colors.error }}>
                      {error}
                    </p>
                  ))}
                </motion.div>
              )}

              {/* Login/SignUp Form */}
              <LoginSignUp
                closeModal={closeModal}
                errors={errors}
                setErrors={setErrors}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoginModal;